"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { getApiErrorMessage } from "@/lib/api/errors";
import {
  ANONYMOUS_MONTHLY_FREE_REQUEST_LIMIT,
  CARD_ON_FILE_MONTHLY_FREE_REQUEST_LIMIT,
  STANDARD_MONTHLY_FREE_REQUEST_LIMIT,
} from "@/lib/api/free-tier";
import AccountFirstRequest from "@/components/account-first-request";

type UsageSummary = {
  billingMode?: "prepaid" | "monthly_postpaid";
  postpaidContract?: {
    status: "active" | "suspended" | "ended";
  } | null;
  account: {
    credits_balance: number;
    usage_requests: number;
    monthly_free_request_limit: number;
    free_tier_kind: "standard" | "anonymous" | "custom" | null;
  };
  monthly_free_usage: {
    limit: number;
    used: number;
    remaining: number;
    enforcedWhenNoCredit: boolean;
    requiresCreditsNow: boolean;
  };
  metrics: {
    errorRate: number;
    noFaceCount?: number;
    avgLatencyMs: number;
  };
};

type BillingSummary = {
  billingMode?: "prepaid" | "monthly_postpaid";
  account: {
    credits_balance: number;
  };
  recentCredits: number;
  postpaidContract?: {
    status: "active" | "suspended" | "ended";
  } | null;
  currentPostpaidPeriod?: {
    billableCalls: number;
    zeroRatedCalls: number;
    estimatedAmountCents: number;
  } | null;
};

const usdCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function formatNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined) return "--";
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) return "--";
  return new Intl.NumberFormat("en-US").format(numeric);
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "--";
  return `${(value * 100).toFixed(1)}%`;
}

function getTrialStatusMessage(
  usage: UsageSummary | null,
  billing: BillingSummary | null,
) {
  if (billing?.billingMode === "monthly_postpaid") {
    if (billing.postpaidContract?.status === "suspended") {
      return "Monthly invoicing is suspended. New postpaid usage is disabled while historical usage and invoices remain available.";
    }
    if (billing.postpaidContract?.status === "ended") {
      return "Monthly invoicing has ended. Historical usage and invoices remain available, but this contract no longer admits new usage.";
    }
    return "Monthly invoicing is active. Completed calls, including no-face detections, use a standard rate of USD $1.02 per 1,000 calls. Priority service costs double; failed calls are zero-rated.";
  }

  const limit = usage?.account.monthly_free_request_limit ?? 0;

  if (limit >= CARD_ON_FILE_MONTHLY_FREE_REQUEST_LIMIT) {
    return "Saved-card unlock is active: your account can use up to 100 free calls while the credit balance stays at or below zero.";
  }

  if (usage?.account.free_tier_kind === "anonymous") {
    return "Anonymous agent access is active: 1 API key and 10 free calls. Upgrade to a real account for 50 free calls, then keep an active saved card for 100 total free calls.";
  }

  if (limit >= STANDARD_MONTHLY_FREE_REQUEST_LIMIT) {
    return "Full account access is active: 50 free calls are available before paid usage kicks in. Keep an active saved card to unlock 100 total free calls.";
  }

  if (limit >= ANONYMOUS_MONTHLY_FREE_REQUEST_LIMIT) {
    return `This account currently has ${formatNumber(limit)} free calls available.`;
  }

  return "Add credits or save a card before sending production traffic.";
}

export default function AccountOverviewPage() {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [usageErrorCode, setUsageErrorCode] = useState<string | null>(null);
  const [billingErrorCode, setBillingErrorCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const usageErrorMessage = useMemo(
    () => getApiErrorMessage(usageErrorCode),
    [usageErrorCode],
  );
  const billingErrorMessage = useMemo(
    () => getApiErrorMessage(billingErrorCode),
    [billingErrorCode],
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setUsageErrorCode(null);
      setBillingErrorCode(null);
      try {
        const [usageResult, billingResult] = await Promise.allSettled([
          fetch("/api/usage", { cache: "no-store" }),
          fetch("/api/billing", { cache: "no-store" }),
        ]);

        if (usageResult.status === "fulfilled") {
          const usagePayload = await usageResult.value.json().catch(() => null);
          if (!usageResult.value.ok || !usagePayload) {
            if (isMounted) {
              setUsageErrorCode(usagePayload?.error ?? "usage_fetch_failed");
            }
          } else if (isMounted) {
            setUsage(usagePayload);
          }
        } else if (isMounted) {
          setUsageErrorCode("usage_fetch_failed");
        }

        if (billingResult.status === "fulfilled") {
          const billingPayload = await billingResult.value
            .json()
            .catch(() => null);
          if (!billingResult.value.ok || !billingPayload) {
            if (isMounted) {
              setBillingErrorCode(
                billingPayload?.error ?? "billing_fetch_failed",
              );
            }
          } else if (isMounted) {
            setBilling(billingPayload);
          }
        } else if (isMounted) {
          setBillingErrorCode("billing_fetch_failed");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      isMounted = false;
    };
  }, []);

  const accountBillingMode = billing?.billingMode ?? usage?.billingMode;
  const postpaidContractStatus =
    billing?.postpaidContract?.status ?? usage?.postpaidContract?.status;
  const postpaidRequestsDisabled =
    accountBillingMode === "monthly_postpaid" &&
    (postpaidContractStatus === "suspended" ||
      postpaidContractStatus === "ended");

  return (
    <div className="space-y-6">
      <section className="px-1 py-2">
        <h1 className="font-[var(--font-display)] text-xl text-[var(--text)] sm:text-2xl">
          Overview
        </h1>
      </section>

      {isLoading ? (
        <section className="panel min-h-36 animate-pulse p-6" role="status">
          <span className="sr-only">Loading account access</span>
        </section>
      ) : postpaidRequestsDisabled ? (
        <section className="panel border border-amber-300/25 bg-amber-400/5 p-6">
          <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-amber-100">
            API requests disabled
          </p>
          <p className="mt-3 text-sm leading-6 text-[var(--muted)]">
            {postpaidContractStatus === "ended"
              ? "This monthly-invoice contract has ended, so it cannot send new API requests. Historical usage and invoices remain available."
              : "This monthly-invoice contract is suspended, so it cannot send new API requests until it is reactivated. Historical usage and invoices remain available."}
          </p>
        </section>
      ) : (
        <AccountFirstRequest
          freeCallsRemaining={usage?.monthly_free_usage.remaining}
          monthlyLimit={usage?.monthly_free_usage.limit}
          isPostpaid={accountBillingMode === "monthly_postpaid"}
          onRequestCompleted={() => {
            void fetch("/api/usage", { cache: "no-store" }).then(async (response) => {
              const data = await response.json();
              if (!response.ok) throw new Error("usage_fetch_failed");
              setUsage(data);
              setUsageErrorCode(null);
            }).catch(() => setUsageErrorCode("usage_fetch_failed"));
          }}
        />
      )}

      {usageErrorMessage ? (
        <section className="panel border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-100">
          <span className="font-semibold text-red-50">Usage data:</span>{" "}
          {usageErrorMessage}
        </section>
      ) : null}

      {billingErrorMessage ? (
        <section className="panel border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-100">
          <span className="font-semibold text-red-50">Billing data:</span>{" "}
          {billingErrorMessage}
        </section>
      ) : null}

      {!isLoading &&
      (billing?.billingMode === "monthly_postpaid" || (billing && usage)) ? (
        <section className="panel p-6">
          <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
            {accountBillingMode === "monthly_postpaid"
              ? "Account status"
              : "Trial status"}
          </p>
          <p className="mt-3 text-sm text-[var(--muted)]">
            {getTrialStatusMessage(usage, billing)}
          </p>
        </section>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <div
          className={`panel flex h-full flex-col p-5 ${isLoading ? "animate-pulse" : ""}`}
        >
          <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
            Usage
          </p>
          <div className="mt-3 space-y-3 text-sm text-[var(--muted)]">
            {isLoading ? (
              <span className="sr-only">Loading</span>
            ) : usage ? (
              <>
                <p>
                  Requests:{" "}
                  <span className="text-[var(--text)]">
                    {formatNumber(usage?.account.usage_requests ?? 0)}
                  </span>
                </p>
                <p>
                  Failure rate:{" "}
                  <span className="text-[var(--text)]">
                    {formatPercent(usage?.metrics.errorRate ?? 0)}
                  </span>
                </p>
                <p>No face found (recent 50): {formatNumber(usage?.metrics.noFaceCount ?? 0)}</p>
                <p>
                  Avg latency:{" "}
                  <span className="text-[var(--text)]">
                    {formatNumber(usage?.metrics.avgLatencyMs ?? 0)}ms
                  </span>
                </p>
                {accountBillingMode === "monthly_postpaid" ? (
                  <p>
                    Billing:{" "}
                    <span className="text-[var(--text)]">
                      {postpaidContractStatus === "suspended"
                        ? "Monthly invoice suspended"
                        : postpaidContractStatus === "ended"
                          ? "Monthly invoice ended"
                          : "Monthly invoice"}
                    </span>
                  </p>
                ) : (
                  <p>
                    Free calls left this month:{" "}
                    <span className="text-[var(--text)]">
                      {`${formatNumber(usage?.monthly_free_usage.remaining ?? 0)} / ${formatNumber(
                        usage?.monthly_free_usage.limit ?? 0,
                      )}`}
                    </span>
                  </p>
                )}
              </>
            ) : (
              <p>Usage data unavailable.</p>
            )}
          </div>
          <Link
            href="/account/usage"
            className="mt-auto pt-5 inline-flex text-xs uppercase tracking-[0.3em] text-[var(--accent)] hover:text-[var(--accent-2)]"
          >
            View usage
          </Link>
        </div>

        <div
          className={`panel flex h-full flex-col p-5 ${isLoading ? "animate-pulse" : ""}`}
        >
          <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
            Billing
          </p>
          <div className="mt-3 space-y-3 text-sm text-[var(--muted)]">
            {isLoading ? (
              <span className="sr-only">Loading</span>
            ) : billing ? (
              <>
                {billing?.billingMode === "monthly_postpaid" ? (
                  <>
                    <p>
                      Billable calls this month:{" "}
                      <span className="text-[var(--text)]">
                        {formatNumber(
                          billing.currentPostpaidPeriod?.billableCalls ?? 0,
                        )}
                      </span>
                    </p>
                    <p>
                      Zero-rated failures:{" "}
                      <span className="text-[var(--text)]">
                        {formatNumber(
                          billing.currentPostpaidPeriod?.zeroRatedCalls ?? 0,
                        )}
                      </span>
                    </p>
                    <p>
                      Accrued this month:{" "}
                      <span className="text-[var(--text)]">
                        {usdCurrencyFormatter.format(
                          (billing.currentPostpaidPeriod
                            ?.estimatedAmountCents ?? 0) / 100,
                        )}
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Credits balance:{" "}
                      <span className="text-[var(--text)]">
                        {formatNumber(billing?.account.credits_balance ?? 0)}
                      </span>
                    </p>
                    <p>
                      Credits last 30 days:{" "}
                      <span className="text-[var(--text)]">
                        {formatNumber(billing?.recentCredits ?? 0)}
                      </span>
                    </p>
                  </>
                )}
              </>
            ) : (
              <p>Billing data unavailable.</p>
            )}
          </div>
          <Link
            href="/account/billing"
            className="mt-auto pt-5 inline-flex text-xs uppercase tracking-[0.3em] text-[var(--accent)] hover:text-[var(--accent-2)]"
          >
            View billing
          </Link>
        </div>

        <div className="panel flex h-full flex-col p-5">
          <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
            MCP
          </p>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Manage tool access and integrations.
          </p>
          <Link
            href="/account/mcp"
            className="mt-auto pt-5 inline-flex text-xs uppercase tracking-[0.3em] text-[var(--accent)] hover:text-[var(--accent-2)]"
          >
            View MCP
          </Link>
        </div>
      </div>
    </div>
  );
}
